// ---------------------------------------------------------------------------
// ADS-012 — Ads spend-approval state machine (Phase 1: constants + vocabulary)
// ---------------------------------------------------------------------------
// Story ADS-012: "Campaign actions that spend money or publish externally
// require explicit user approval and audit."
//
// Per docs/vision_execution/openclaw/ADS-012-DECISION-PACKET.md the gap is
// NOT a route to gate. As of 2026-05-13 the repo has ZERO mounted external
// spend-mutating ad routes — `workers/api-analytics/src/routes/ads/*` are
// all read-only (`/dashboard`, `/campaigns`, `/performance`, `/anomalies`,
// `/generate`), `POST /anomalies/:id/snooze` is state-only, and CAPI is
// measurement-only.
//
// The packet locks a **gate-before-route design contract**: the first PR
// that adds a spend-mutating route MUST ship the gate in the same PR. This
// Phase 1 file pre-builds the vocabulary that future PR will reference so
// the gate cannot drift state-machine names, mutation-kind enums, or audit-
// event semantics from SOC-005 (which uses the exact same state machine on
// social publishing).
//
// Phase 1 scope (this file): pure constant vocabulary + state-transition
// guard. No Drizzle table — the future PR introduces
// `ad_pending_mutations` + `ad_pending_mutation_history` mirroring the
// SOC-005 / AN-020 schema pattern. Phases 2+ owed per packet:
//
//   Phase 2 -> `ad_pending_mutations` + `ad_pending_mutation_history`
//              Drizzle tables (idempotent on `(tenant_id, target_kind,
//              target_id, change_payload_hash)`, append-only history with
//              per-tenant + per-mutation indexes).
//   Phase 3 -> `requireSpendApproval` middleware enforcing the gate on
//              every spend-mutating handler (mutation_id required from
//              caller, self-approval blocked via `requester_id !==
//              approver_id`, `requireRole("admin")`).
//   Phase 4 -> provider-SDK refusal guard at `packages/ads-meta/`,
//              `packages/ads-google/` compile-time `mutationId: string` arg.
//   Phase 5 -> 24h TTL cron worker auto-expiring pending mutations.
//   Phase 6 -> 6 CQRS event types catalogued in `@ease/events`.
//
// Locking the vocabulary first means SOC-005 and ADS-012 share ONE source
// of truth for state-machine names — drift across the two surfaces fails
// loudly via the cross-registry coverage matrix.
// ---------------------------------------------------------------------------

/**
 * Closed approval/execution-state vocabulary. The approval prefix mirrors
 * SOC-005 social-publish approval, while `applied` / `failed_at_provider`
 * are ADS-012-specific provider execution terminals.
 *
 * Source: ADS-012-DECISION-PACKET §3 (State machine).
 */
export const ADS_APPROVAL_STATES = [
  "pending_approval",
  "approved",
  "applied",
  "failed_at_provider",
  "rejected",
  "expired",
] as const;

export type AdsApprovalState = (typeof ADS_APPROVAL_STATES)[number];

/**
 * Runtime guard for the closed approval-state enum. Used at API boundaries
 * (HTTP body, queue payload, KV cache value).
 */
export function isAdsApprovalState(value: unknown): value is AdsApprovalState {
  return (
    typeof value === "string" &&
    (ADS_APPROVAL_STATES as readonly string[]).includes(value)
  );
}

/**
 * Closed mutation-kind catalogue. Covers every spend-mutating or external-
 * publish operation a future ads route can perform. New mutation kinds MUST
 * extend this list AND ship a corresponding handler — drift fails the
 * cross-registry coverage matrix.
 *
 * Source: ADS-012-DECISION-PACKET §4 (Mutation taxonomy).
 */
export const ADS_MUTATION_KINDS = [
  "budget_increase",
  "budget_decrease",
  "campaign_launch",
  "campaign_pause",
  "campaign_resume",
  "campaign_archive",
  "creative_publish",
  "audience_update",
] as const;

export type AdsMutationKind = (typeof ADS_MUTATION_KINDS)[number];

export function isAdsMutationKind(value: unknown): value is AdsMutationKind {
  return (
    typeof value === "string" &&
    (ADS_MUTATION_KINDS as readonly string[]).includes(value)
  );
}

/**
 * Closed target-kind catalogue for the operation identity unique index.
 * `mutation_kind` captures the requested operation; `target_kind` groups
 * equivalent spend/publish surfaces so retries cannot substitute approvals
 * across the same provider object with the same payload hash.
 */
export const ADS_MUTATION_TARGET_KINDS = [
  "budget_change",
  "campaign_status",
  "creative_publish",
  "audience_update",
] as const;

export type AdsMutationTargetKind =
  (typeof ADS_MUTATION_TARGET_KINDS)[number];

export function isAdsMutationTargetKind(
  value: unknown,
): value is AdsMutationTargetKind {
  return (
    typeof value === "string" &&
    (ADS_MUTATION_TARGET_KINDS as readonly string[]).includes(value)
  );
}

/**
 * Closed rejection-reason catalogue. Provider SDK refusal guards write one
 * of these into the audit row when a mutation is denied. Free-form text is
 * intentionally NOT permitted — auditors need a fixed vocabulary to query.
 */
export const ADS_APPROVAL_REJECTION_REASONS = [
  "self_approval_blocked",
  "approver_not_admin",
  "mutation_already_decided",
  "ttl_expired",
  "provider_quota_exceeded",
  "policy_violation",
  "tenant_paused",
  "no_pending_record",
] as const;

export type AdsApprovalRejectionReason =
  (typeof ADS_APPROVAL_REJECTION_REASONS)[number];

/**
 * Closed audit event-type vocabulary. The Phase 6 CQRS catalog re-exports
 * these as the canonical event-type strings.
 */
export const ADS_APPROVAL_EVENTS = [
  "ads.mutation.requested@v1",
  "ads.mutation.approved@v1",
  "ads.mutation.rejected@v1",
  "ads.mutation.expired@v1",
  "ads.mutation.applied@v1",
  "ads.mutation.failed_at_provider@v1",
] as const;

export type AdsApprovalEvent = (typeof ADS_APPROVAL_EVENTS)[number];

/**
 * TTL for a pending mutation before the Phase 5 cron worker auto-expires
 * it. 24h matches SOC-005 social publish approval window.
 */
export const ADS_APPROVAL_TTL_HOURS = 24;
export const ADS_APPROVAL_TTL_MS = ADS_APPROVAL_TTL_HOURS * 60 * 60 * 1000;

/**
 * State-transition guard pinning the decision packet's state machine. Any
 * future PR that widens the legal transition set must update this guard AND
 * the proof test below in lockstep.
 *
 * Legal transitions:
 *   pending_approval -> approved            (admin approves, not requester)
 *   pending_approval -> rejected            (admin rejects)
 *   pending_approval -> expired             (cron worker after TTL)
 *   approved         -> applied             (provider SDK succeeds)
 *   approved         -> failed_at_provider  (provider SDK fails closed)
 *   approved         -> approved            (idempotent queue retry, no-op)
 *   applied          -> applied             (idempotent provider replay, no-op)
 *   failed_at_provider -> failed_at_provider (idempotent failure replay)
 *   rejected         -> rejected            (idempotent re-decision, no-op)
 *   expired          -> expired             (idempotent post-TTL no-op)
 *
 * Illegal (raises a hard error in the future middleware):
 *   anything else, especially:
 *     approved -> rejected, rejected -> approved (decision is sticky)
 *     expired -> approved (cannot resurrect a TTL'd mutation)
 *     approved -> pending_approval (cannot regress to undecided)
 */
export function isLegalAdsApprovalTransition(
  current: AdsApprovalState,
  next: AdsApprovalState,
): boolean {
  if (current === next) {
    return (
      current === "approved" ||
      current === "applied" ||
      current === "failed_at_provider" ||
      current === "rejected" ||
      current === "expired"
    );
  }
  if (current === "pending_approval") {
    return next === "approved" || next === "rejected" || next === "expired";
  }
  if (current === "approved") {
    return next === "applied" || next === "failed_at_provider";
  }
  return false;
}
